import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer, Subject, EMPTY, defer } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, retry, switchMap, takeUntil } from 'rxjs/operators';
import { HttpCookieService } from './general/http-cookie.service';
import { AUTH_COOKIE_CSRF_TOKEN } from '../utils/constants/authentication/authentication.constants';
import { Capacitor } from '@capacitor/core';

const MAX_NETWORK_RETRIES = 4;
export const MAX_AUTH_RETRIES = 1; // Number of retries for 401/403 errors
export const NETWORK_RETRY_DELAY_MS = 3000;
const HTTP_STATUS_UNAUTHORIZED = 401;
const HTTP_STATUS_FORBIDDEN = 403;
const HTTP_STATUS_NETWORK_ERROR = 0;

@Injectable({
  providedIn: 'root',
})
export class HttpRequestInterceptorService implements HttpInterceptor {
  private retryCount = new WeakMap<HttpRequest<any>, number>();
  private cancelAllRequests$ = new Subject<void>();
  private isHandlingAuthError = false; // Flag to prevent multiple simultaneous calls

  constructor(private router: Router, private httpCookieService: HttpCookieService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(this.addTokenToRequest(request)).pipe(
      takeUntil(this.cancelAllRequests$),
      retry({ count: MAX_NETWORK_RETRIES, delay: this.delayNotifier }),
      catchError((error: HttpErrorResponse) => this.handleError(error, request, next))
    );
  }

  private handleError(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.isAuthError(error)) {
      return this.handleAuthErrorWithRetry(error, request, next);
    }

    return throwError(() => error);
  }

  private isAuthError(error: HttpErrorResponse): boolean {
    return error.status === HTTP_STATUS_UNAUTHORIZED || error.status === HTTP_STATUS_FORBIDDEN;
  }

  private handleAuthErrorWithRetry(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // If auth error is already being handled, just throw error
    if (this.isHandlingAuthError) {
      return throwError(() => error);
    }

    const currentRetryCount = this.retryCount.get(request) ?? 0;

    if (currentRetryCount >= MAX_AUTH_RETRIES) {
      // On native iOS a persistent 403 means the user lacks a role for this endpoint
      // (e.g. non-Learner hitting a Learner-only API). Let the service's catchError
      // handle it gracefully instead of logging the user out.
      // Web behaviour is preserved: 403 on web still triggers logout as before.
      if (error.status === HTTP_STATUS_FORBIDDEN && Capacitor.getPlatform() === 'ios') {
        return throwError(() => error);
      }
      this.handleAuthError();
      return throwError(() => error);
    }

    // Increment retry count
    this.retryCount.set(request, currentRetryCount + 1);

    // Use defer to create timer lazily, checking cancellation at creation time
    return defer(() => {
      // Check if cancellation has been triggered - return EMPTY if so
      if (this.cancelAllRequests$.closed) {
        return EMPTY;
      }
      return timer(NETWORK_RETRY_DELAY_MS);
    }).pipe(
      takeUntil(this.cancelAllRequests$),
      switchMap(() => {
        // Double-check cancellation before retrying - return EMPTY if cancelled
        if (this.cancelAllRequests$.closed) {
          return EMPTY;
        }
        return this.retryRequest(request, next);
      })
    );
  }

  private retryRequest(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(this.addTokenToRequest(request)).pipe(
      takeUntil(this.cancelAllRequests$),
      catchError((retryError: HttpErrorResponse) => {
        // If retry also fails with auth error, recursively try again
        if (this.isAuthError(retryError)) {
          return this.handleAuthErrorWithRetry(retryError, request, next);
        }
        // For non-auth errors, throw immediately
        return throwError(() => retryError);
      })
    );
  }

  private addTokenToRequest(request: HttpRequest<any>): HttpRequest<any> {
    const csrfToken = this.httpCookieService.getCookie(AUTH_COOKIE_CSRF_TOKEN);

    if (!csrfToken) {
      return request.clone({ withCredentials: true });
    }

    return request.clone({
      withCredentials: true,
      setHeaders: { 'X-CSRF-Token': csrfToken },
    });
  }

  private handleAuthError(): void {
    // Prevent multiple simultaneous calls to handleAuthError
    if (this.isHandlingAuthError) {
      return;
    }

    this.isHandlingAuthError = true;

    // Cancel all pending requests and observables
    this.cancelAllPendingRequests();

    // Clear localStorage
    localStorage.clear();
    window.location.replace('/logout');
  }

  private cancelAllPendingRequests(): void {
    // Only cancel if Subject is not already closed
    if (!this.cancelAllRequests$.closed) {
      // Signal all observables to complete/cancel
      // This will trigger takeUntil in all active observables
      this.cancelAllRequests$.next();
      this.cancelAllRequests$.complete();
    }

    // Create new Subject for future requests (after logout/login)
    this.cancelAllRequests$ = new Subject<void>();
    // Reset the flag when creating a new Subject (after logout/login)
    this.isHandlingAuthError = false;
  }

  private delayNotifier = (error: HttpErrorResponse, retryCount: number) => {
    if (error.status === HTTP_STATUS_NETWORK_ERROR) {
      // Use defer to create timer lazily, checking cancellation at creation time
      return defer(() => {
        // Check if cancellation has been triggered - return EMPTY if so
        if (this.cancelAllRequests$.closed) {
          return EMPTY;
        }
        return timer(NETWORK_RETRY_DELAY_MS);
      }).pipe(
        takeUntil(this.cancelAllRequests$)
      );
    }
    throw error;
  };
}
